import { useTheme } from 'foundations-theming/utils';
import { memo, useCallback, useMemo } from 'react';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
// @ts-expect-error Untyped dependency
import PhoneNumberInput from 'ui-addon-i18n/components/PhoneNumberInput';
import FormattedMessage from 'I18n/components/FormattedMessage';
import unescapedText from 'I18n/utils/unescapedText';
import styled from 'foundations-theming/utils';
import { isValidRawPhoneNumber, parseRawPhoneNumber } from './phone/utils/phoneNumberUtils';
import { Button } from 'foundations-components/transitional/components/Button';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { FETCH_STATUS } from './phone/hooks/usePhoneNumberValidator';
import { Tile } from 'foundations-components/transitional/components/Tile';
import UITileSection from 'UIComponents/tile/UITileSection';
import TextInputAdapter from '../adapters/TextInput';
import { InfoIcon } from 'foundations-assets';
import { View } from 'foundations-components/transitional/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledInfoIconWrapper = styled.span.withConfig({
  displayName: "PropertyInputPhone__StyledInfoIconWrapper",
  componentId: "sc-1w5etut-0"
})(["margin-inline-start:", ";"], ({
  theme
}) => theme.space['200']);
const Wrapper = styled.div.withConfig({
  displayName: "PropertyInputPhone__Wrapper",
  componentId: "sc-1w5etut-1"
})(["min-width:359px;"]);
const StyledUITileSection = styled(UITileSection).withConfig({
  displayName: "PropertyInputPhone__StyledUITileSection",
  componentId: "sc-1w5etut-2"
})(["padding:14px 10px 10px 10px;"]);
export const getPhoneNumberString = (phoneNumber, extension) => extension !== null && extension !== '' && extension !== undefined ? `${phoneNumber} ext ${extension}` : phoneNumber || '';
const PropertyInputPhone = ({
  disabled,
  value,
  initialCountryDialCode,
  status,
  error,
  showTextInput,
  onChange,
  onToggleInput
}) => {
  const trellisTheme = useTheme();
  const {
    rawValue,
    extensionValue
  } = useMemo(() => {
    if (isValidRawPhoneNumber(value)) {
      const {
        phoneNumber,
        extension
      } = parseRawPhoneNumber(value);
      return {
        rawValue: phoneNumber,
        extensionValue: extension
      };
    }
    return {
      rawValue: value,
      extensionValue: ''
    };
  }, [value]);
  const handleTextChange = useCallback(evt => {
    onChange(evt.target.value);
  }, [onChange]);
  const handlePhoneChange = useCallback(({
    phoneNumber,
    extension,
    country
  }) => {
    const newPhoneNumber = getPhoneNumberString(phoneNumber, extension);
    onChange(newPhoneNumber, country);
  }, [onChange]);
  const renderErrorMessage = useMemo(() => {
    const isPending = status === FETCH_STATUS.PENDING;
    const isSucceeded = status === FETCH_STATUS.SUCCEEDED;
    if (value === null || value === undefined || value === '' || value === initialCountryDialCode) {
      return /*#__PURE__*/_jsx("span", {
        style: {
          color: trellisTheme.color['do-not-use-slinky']
        },
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "customerDataProperties.PropertyInputPhone.phoneNumberValidations.prevalidation",
          "data-unit-test": "prevalidation-message"
        })
      });
    }
    if ((isPending || isSucceeded) && error) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "customerDataProperties.PropertyInputPhone.phoneNumberErrors.numberInvalid",
        "data-unit-test": "error-number-invalid"
      });
    }
    if (error) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "customerDataProperties.PropertyInputPhone.phoneNumberErrors.validationFailed"
      });
    }
    if (isPending) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "customerDataProperties.PropertyInputPhone.phoneNumberValidations.validating"
      });
    }
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "customerDataProperties.PropertyInputPhone.phoneNumberValidations.validated"
    });
  }, [status, error, value, initialCountryDialCode, trellisTheme.color]);
  const message = showTextInput ? 'customerDataProperties.PropertyInputPhone.editOptions.editAsPhoneNumber.btn' : 'customerDataProperties.PropertyInputPhone.editOptions.editAsText.btn';
  const tooltip = showTextInput ? 'customerDataProperties.PropertyInputPhone.editOptions.editAsPhoneNumber.tooltip' : 'customerDataProperties.PropertyInputPhone.editOptions.editAsText.tooltip';
  return /*#__PURE__*/_jsxs(Wrapper, {
    children: [showTextInput ? /*#__PURE__*/_jsx(TextInputAdapter, {
      disabled: disabled,
      value: value,
      onChange: handleTextChange,
      autoFocus: true
    }) : /*#__PURE__*/_jsx(Tile, {
      compact: true,
      children: /*#__PURE__*/_jsx(StyledUITileSection, {
        children: /*#__PURE__*/_jsx(FormControl, {
          error: error,
          validationMessage: renderErrorMessage,
          "aria-label": unescapedText('customerDataProperties.PropertyInputPhone.ariaLabel'),
          children: /*#__PURE__*/_jsx("div", {
            className: "display-flex",
            children: /*#__PURE__*/_jsx(PhoneNumberInput, {
              value: rawValue || initialCountryDialCode,
              extensionValue: extensionValue,
              phoneInputProps: {
                disabled,
                autoFocus: true
              },
              onChange: handlePhoneChange,
              countrySelectProps: {
                disabled
              },
              extensionInputProps: {
                disabled
              }
            })
          })
        })
      })
    }), /*#__PURE__*/_jsxs(View, {
      marginBlockStart: "300",
      children: [/*#__PURE__*/_jsx(Button, {
        use: "link",
        onClick: onToggleInput,
        "data-selenium-test": "toggle-input-type-button",
        disabled: status === FETCH_STATUS.PENDING,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: message
        })
      }), /*#__PURE__*/_jsx(Tooltip, {
        placement: "bottom",
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: tooltip
        }),
        children: /*#__PURE__*/_jsx(StyledInfoIconWrapper, {
          children: /*#__PURE__*/_jsx(InfoIcon, {
            alignWithText: true,
            color: 'icon-core-subtle'
          })
        })
      })]
    })]
  });
};
export default /*#__PURE__*/memo(PropertyInputPhone);